import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Receipt, Plus, Trash2, Edit, ExternalLink, Search, Download } from 'lucide-react';

const STATUSES = ['DRAFT', 'SENT', 'ACCEPTED', 'REJECTED', 'EXPIRED'];

const statusColors = {
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-200',
  SENT: 'bg-blue-100 text-blue-700 border-blue-200',
  ACCEPTED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  REJECTED: 'bg-red-100 text-red-700 border-red-200',
  EXPIRED: 'bg-amber-100 text-amber-700 border-amber-200',
};

const QuotesPage = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // Edit dialog
  const [editQuote, setEditQuote] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', summary: '', notes: '', line_items: [] });

  const fetchQuotes = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await quotesAPI.list(params);
      setQuotes(res.data);
    } catch { toast.error('Failed to load quotes'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const filtered = search
    ? quotes.filter(q => q.title?.toLowerCase().includes(search.toLowerCase()) || q.task_title?.toLowerCase().includes(search.toLowerCase()))
    : quotes;

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await quotesAPI.updateStatus(quoteId, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      fetchQuotes();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Delete this quote?')) return;
    try {
      await quotesAPI.delete(quoteId);
      toast.success('Quote deleted');
      fetchQuotes();
    } catch { toast.error('Failed to delete quote'); }
  };

  const handleDownloadPdf = async (quote) => {
    try {
      const res = await quotesAPI.downloadPdf(quote.quote_id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quote.title || 'quote'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
  };

  const openEdit = (quote) => {
    setEditQuote(quote);
    setEditForm({
      title: quote.title || '',
      summary: quote.summary || '',
      notes: quote.notes || '',
      line_items: (quote.line_items || []).map(li => ({ ...li })),
    });
  };

  const handleSaveEdit = async () => {
    try {
      await quotesAPI.update(editQuote.quote_id, editForm);
      toast.success('Quote updated');
      setEditQuote(null);
      fetchQuotes();
    } catch { toast.error('Failed to update quote'); }
  };

  const updateLineItem = (idx, field, value) => {
    const items = [...editForm.line_items];
    items[idx] = { ...items[idx], [field]: value };
    setEditForm({ ...editForm, line_items: items });
  };

  const addLineItem = () => {
    setEditForm({
      ...editForm,
      line_items: [...editForm.line_items, { description: '', quantity: 1, unit_price: 0 }],
    });
  };

  const removeLineItem = (idx) => {
    setEditForm({ ...editForm, line_items: editForm.line_items.filter((_, i) => i !== idx) });
  };

  const editSubtotal = editForm.line_items.reduce((sum, li) => sum + (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-heading">Quotes</h1>
        <p className="text-slate-600">Manage and track your client quotes</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search quotes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="quotes-search"
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="quotes-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Quotes List */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-600 font-medium">No quotes yet</p>
            <p className="text-sm text-slate-400 mt-1">Generate a quote from the AI Assist tab on any task</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((quote) => (
            <Card key={quote.quote_id} className="hover:shadow-sm transition-shadow" data-testid={`quote-row-${quote.quote_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-slate-900 truncate">{quote.title}</h3>
                      <Badge className={statusColors[quote.status] || statusColors.DRAFT}>
                        {quote.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      {quote.task_title && (
                        <button
                          className="flex items-center gap-1 text-indigo-600 hover:underline"
                          onClick={() => navigate(`/dashboard/tasks?task=${quote.task_id}`)}
                        >
                          <ExternalLink className="h-3 w-3" />
                          {quote.task_title}
                        </button>
                      )}
                      <span>{format(new Date(quote.created_at), 'PP')}</span>
                      <span>{quote.line_items?.length || 0} items</span>
                    </div>
                    {quote.summary && (
                      <p className="text-sm text-slate-500 mt-1 line-clamp-1">{quote.summary}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-900">${(quote.total || 0).toFixed(2)}</p>
                      {quote.gst_registered && quote.gst_amount > 0 && (
                        <p className="text-xs text-slate-500">{quote.gst_inclusive ? 'inc.' : '+'} ${quote.gst_amount.toFixed(2)} GST</p>
                      )}
                    </div>
                    <Select value={quote.status} onValueChange={(val) => handleStatusChange(quote.quote_id, val)}>
                      <SelectTrigger className={`w-32 h-8 text-xs ${statusColors[quote.status]}`} data-testid={`quote-status-${quote.quote_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(quote)} data-testid={`quote-edit-${quote.quote_id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(quote)} data-testid={`quote-pdf-${quote.quote_id}`}>
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(quote.quote_id)}>
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Quote Dialog */}
      <Dialog open={!!editQuote} onOpenChange={(open) => { if (!open) setEditQuote(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Title</Label>
              <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="mt-1" data-testid="edit-quote-title" />
            </div>
            <div>
              <Label>Summary</Label>
              <Textarea value={editForm.summary} onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })} rows={2} className="mt-1" />
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line Items</Label>
                <Button variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item-btn">
                  <Plus className="mr-1 h-3 w-3" /> Add Item
                </Button>
              </div>
              {editForm.line_items.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No line items. Click "Add Item" to start.</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 text-xs text-slate-500 font-medium px-1">
                    <span className="col-span-5">Description</span>
                    <span className="col-span-2">Qty</span>
                    <span className="col-span-2">Unit Price</span>
                    <span className="col-span-2 text-right">Total</span>
                    <span className="col-span-1"></span>
                  </div>
                  {editForm.line_items.map((li, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`line-item-${idx}`}>
                      <Input className="col-span-5 h-8 text-sm" value={li.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} placeholder="Description" />
                      <Input className="col-span-2 h-8 text-sm" type="number" min="0" value={li.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)} />
                      <Input className="col-span-2 h-8 text-sm" type="number" min="0" step="0.01" value={li.unit_price} onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)} />
                      <span className="col-span-2 text-sm font-medium text-right">${((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0)).toFixed(2)}</span>
                      <Button variant="ghost" size="sm" className="col-span-1 h-8 w-8 p-0" onClick={() => removeLineItem(idx)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t">
                    <div className="text-right space-y-1">
                      <div className="text-base font-bold">Subtotal: ${editSubtotal.toFixed(2)}</div>
                      {editQuote?.gst_registered && (
                        <>
                          <div className="text-sm text-slate-500">
                            GST ({editQuote.gst_rate || 10}%): ${(editQuote.gst_inclusive
                              ? editSubtotal * (editQuote.gst_rate || 10) / (100 + (editQuote.gst_rate || 10))
                              : editSubtotal * (editQuote.gst_rate || 10) / 100
                            ).toFixed(2)}
                          </div>
                          <div className="text-base font-bold">
                            Total (inc. GST): ${(editQuote.gst_inclusive
                              ? editSubtotal
                              : editSubtotal + editSubtotal * (editQuote.gst_rate || 10) / 100
                            ).toFixed(2)}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label>Notes / Terms</Label>
              <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} className="mt-1" placeholder="Payment terms, conditions, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditQuote(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} data-testid="save-quote-edit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotesPage;
